import React, {useCallback, useContext, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import {EntityDrawer} from "features/drawers";
import IncidentDetectionDrawer from "features/drawers/IncidentDetectionDrawer/IncidentDetectionDrawer";
import EventsTablePanel from "features/investigations/InvestigationDetail/EventsTablePanel";
import InvestigateDetailHeader from "features/investigations/InvestigationDetail/InvestigateDetailHeader";
import InvestigateGraphPanel from "features/investigations/InvestigationDetail/InvestigateGraphPanel";
import InvestigationNotFound from "features/investigations/InvestigationDetail/InvestigationGraph/NotFound/InvestigationNotFound";
import {InvestigationDetailContext} from "contexts/InvestigationDetailContext";
import {RelationsGraphProvider} from "contexts/RelationsGraphContext";
import useDrawer from "hooks/useDrawer";
import useOverflowDrawer from "hooks/useOverflowDrawer";
import {
  clearInvestigation,
  getInvestigationStatus,
  getSavedInvestigation
} from "store/features/investigationSlice";
import {
  selectIncidentNotFound,
  selectInvestigationLoading,
  selectHasInvestigation as selectIsInvestigationValid
} from "store/selectors/investigationSelectors";
import css from "./InvestigationDetail.module.scss";

const InvestigationDetail = ({id}) => {
  const dispatch = useDispatch();

  const {isFullscreen, isSavedInvestigation} = useContext(
    InvestigationDetailContext
  );

  const isLoading = useSelector(selectInvestigationLoading);
  const isNotFound = useSelector(selectIncidentNotFound);
  const isValid = useSelector(selectIsInvestigationValid);

  const [entityDrawerDataType, setEntityDrawerDataType] = useState();

  /** Main Incident Detection Drawer */
  const {
    overflowContent,
    addOverflowContent,
    removeOverflowContent,
    overflowLayersCount,
    isOpen: isDetectionDrawerOpen,
    open: openDetectionDrawer,
    close: closeDetectionDrawer,
    data: selectedEvent
  } = useOverflowDrawer();

  /** EntityDrawer */
  const {
    isOpen: isEntityDrawerOpen,
    open: openEntityDrawer,
    close: closeEntityDrawer,
    data: entityDrawerData
  } = useDrawer();

  /**
   * This instance of Entity Drawer may render any EntityType
   * depending which dataset is passed 'onOpen'.
   * For that reason, also track the `entityDrawerDataType` selected
   * so that the EntityDrawer component knows the data type to render.
   *
   * `entityDrawerDataType` should always be set in the same action as opening
   * the drawer with a new dataset.
   */
  const handleOpenEntityDrawer = useCallback(
    (data, entityType) => {
      const doOpen = () =>
        openEntityDrawer(data).then(() => setEntityDrawerDataType(entityType));
      isDetectionDrawerOpen ? closeDetectionDrawer().then(doOpen) : doOpen();
    },
    [closeDetectionDrawer, isDetectionDrawerOpen, openEntityDrawer]
  );

  const handleOpenDetectionDrawer = useCallback(
    (data) => {
      const doOpen = () => openDetectionDrawer(data);
      isEntityDrawerOpen ? closeEntityDrawer().then(doOpen) : doOpen();
    },
    [closeEntityDrawer, isEntityDrawerOpen, openDetectionDrawer]
  );

  useEffect(() => {
    if (id) {
      /**
       * 1.) If isSavedInvestigation === true: Bypass polling and dispatch an action to
       *     fetch the existing investigation data.
       * 2.) If isSavedInvestigation === false: We can currently assume a new investigation has been created.
       *     Dispatch an action to initiate polling of the Investigation status.
       */
      dispatch(
        isSavedInvestigation
          ? getSavedInvestigation({id})
          : getInvestigationStatus({id})
      );

      return () => {
        // Cleanup when page unmounts (i.e. user navigates back to '/investigate')
        dispatch(clearInvestigation()); // Reset store. All flags need to be reset to initialState.
      };
    }
  }, []);

  if (isNotFound) {
    return <InvestigationNotFound />;
  }

  return (
    !isLoading &&
    isValid && (
      <div data-testid={"InvestigationDetail-Panel"} className={css.grid}>
        {!isFullscreen && (
          <InvestigateDetailHeader className={css.gridHeadCell} />
        )}
        <RelationsGraphProvider>
          <InvestigateGraphPanel className={css.graphCell} />
        </RelationsGraphProvider>
        {!isFullscreen && (
          <>
            <EventsTablePanel
              className={css.eventsCell}
              openEntityDrawer={handleOpenEntityDrawer}
              openDetectionDrawer={handleOpenDetectionDrawer}
            />
            <IncidentDetectionDrawer
              overflowLayersCount={overflowLayersCount}
              overflowContent={overflowContent}
              event={selectedEvent}
              addOverflowContent={addOverflowContent}
              removeOverflowContent={removeOverflowContent}
              isOpen={isDetectionDrawerOpen}
              onClose={closeDetectionDrawer}
              openEntityDrawer={handleOpenEntityDrawer}
            />
            <EntityDrawer
              count={entityDrawerData?.length || 0}
              data={entityDrawerData}
              entityType={entityDrawerDataType}
              isOpen={isEntityDrawerOpen}
              onClose={closeEntityDrawer}
              entityListProps={{
                showEventsCount: false,
                tester: 5
              }}
            />
          </>
        )}
      </div>
    )
  );
};

InvestigationDetail.propTypes = {
  id: PropTypes.string.isRequired
};

export default InvestigationDetail;
